import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Send, Upload, FileText, Menu, X, LogOut, Trash2,
  FolderOpen, MessageSquare, TrendingUp, User, MoreVertical, Paperclip,
  Lightbulb, Users, Target, Clock, CheckCircle, AlertTriangle, HelpCircle,
  BookOpen, UploadCloud, ChevronRight, Sparkles, Image, Download, FileOutput,
  Settings, BarChart3, Shield, Lock, Info
} from 'lucide-react';
import GuidedMode from '@/components/GuidedMode';
import ProjectOnboarding from '@/components/ProjectOnboarding';
import JourneyProgress from '@/components/JourneyProgress';
import InsightsDrawer from '@/components/InsightsDrawer';
import DocumentsPanel from '@/components/DocumentsPanel';
import ContactSLPModal from '@/components/ContactSLPModal';
import { StepsDisplay, parseStepsFromContent } from '@/components/StepsDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suggested next actions based on journey stage
const STAGE_PROMPTS = {
  understand: [
    { icon: Target, text: "Help me clarify what's changing", action: "clarify" },
    { icon: Users, text: "Who else should be involved?", action: "stakeholders" },
  ],
  assess: [
    { icon: Sparkles, text: "Start impact assessment", action: "impact_assessment" },
    { icon: AlertTriangle, text: "What risks should I watch for?", action: "risks" },
  ],
  plan: [
    { icon: FileText, text: "Help me build a change plan", action: "change_plan" },
    { icon: MessageSquare, text: "What should I communicate?", action: "comms" },
  ],
  engage: [
    { icon: Users, text: "Create stakeholder pack", action: "stakeholder_pack" },
    { icon: HelpCircle, text: "How do I brief my sponsor?", action: "sponsor" },
  ],
  deliver: [
    { icon: CheckCircle, text: "Am I ready for go-live?", action: "readiness" },
    { icon: AlertTriangle, text: "What could go wrong?", action: "risks" },
  ],
  embed: [
    { icon: Target, text: "How do I make this stick?", action: "reinforce" },
    { icon: CheckCircle, text: "How do I measure success?", action: "measure" },
  ],
};

export default function Dashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [showGuidedMode, setShowGuidedMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [journeyStage, setJourneyStage] = useState('understand');
  const [completedTasks, setCompletedTasks] = useState(['name', 'description']);
  const [showDocumentPrompt, setShowDocumentPrompt] = useState(false);
  const [uploadedDocType, setUploadedDocType] = useState('');
  const [showInsights, setShowInsights] = useState(false);
  const [selectedInsightStage, setSelectedInsightStage] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);  // Images waiting to be sent
  const [showDocuments, setShowDocuments] = useState(false);
  const [showContactSLP, setShowContactSLP] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Hide quick prompts after first user message
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length > 0) {
      setShowPrompts(false);
    }
  }, [messages]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    }
  }, []);

  const fetchUsageStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/usage`);
      setUsageStats(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  }, []);

  const fetchProject = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setCurrentProject(response.data.project);
      setMessages(response.data.messages);
      // Show prompts if no user messages yet
      const userMsgs = response.data.messages.filter(m => m.role === 'user');
      setShowPrompts(userMsgs.length === 0);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project');
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchProjects();
      await fetchUsageStats();
      if (projectId) {
        await fetchProject(projectId);
      }
      setLoading(false);
    };
    init();
  }, [projectId, fetchProjects, fetchProject, fetchUsageStats]);

  // Create project from onboarding flow
  const handleOnboardingComplete = async (data) => {
    try {
      const response = await axios.post(`${API}/projects`, {
        name: data.name,
        description: data.description || ''
      });
      setProjects([response.data, ...projects]);
      setShowOnboarding(false);
      navigate(`/dashboard/${response.data.id}`);
      
      // Send initial context to the coach
      const contextMessage = `I'm starting a new change initiative called "${data.name}". 
${data.description ? `Here's what's changing: ${data.description}` : ''}
${data.affected ? `The people affected are: ${data.affected}` : ''}
${data.timeline ? `Timeline: ${data.timeline}` : ''}
${data.stage ? `We're currently at the "${data.stage}" stage.` : ''}

What should I focus on first?`;
      
      // Small delay to let the project load
      setTimeout(() => {
        sendMessage(contextMessage);
      }, 1000);
      
      toast.success("Great! Let's get started on your change journey.");
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project and all its conversations?')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${id}`);
      setProjects(projects.filter(p => p.id !== id));
      if (currentProject?.id === id) {
        setCurrentProject(null);
        setMessages([]);
        navigate('/dashboard');
      }
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const sendMessage = async (content = null) => {
    const messageContent = content || inputMessage;
    if (!messageContent.trim() || !currentProject) return;

    setInputMessage('');
    setSending(true);
    setShowPrompts(false);

    // Add user message immediately with images preview
    const tempUserMessage = {
      id: 'temp-user',
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      image_paths: pendingImages.map(img => img.preview)
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      // Upload images first if any
      const uploadedImagePaths = [];
      for (const img of pendingImages) {
        const formData = new FormData();
        formData.append('file', img.file);
        const uploadRes = await axios.post(`${API}/projects/${currentProject.id}/upload-image`, formData);
        uploadedImagePaths.push(uploadRes.data.path);
      }
      
      const response = await axios.post(`${API}/projects/${currentProject.id}/chat`, {
        content: messageContent,
        project_id: currentProject.id,
        image_paths: uploadedImagePaths.length > 0 ? uploadedImagePaths : null
      });
      
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-user'),
        { ...tempUserMessage, id: `user-${Date.now()}`, image_paths: uploadedImagePaths },
        response.data
      ]);
      setPendingImages([]);  // Clear pending images after send
      fetchProjects();
      fetchUsageStats();  // Update usage counter
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      setInputMessage(messageContent);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    sendMessage(prompt.text);
  };

  const handleFileUpload = async (fileType) => {
    fileInputRef.current.dataset.fileType = fileType;
    fileInputRef.current.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentProject) return;

    const fileType = e.target.dataset.fileType;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    setUploading(true);
    try {
      await axios.post(`${API}/projects/${currentProject.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await fetchProject(currentProject.id);
      toast.success('Document uploaded successfully!');
      
      // Show prompt asking if they want to review - don't auto-analyze
      setShowDocumentPrompt(true);
      setUploadedDocType('Document');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload file';
      toast.error(message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Image/screenshot upload handler
  const onImageSelected = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !currentProject) return;
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setPendingImages(prev => [...prev, ...newImages]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setPendingImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items || !currentProject) return;
    
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const newImage = {
            file,
            preview: URL.createObjectURL(file),
            name: `screenshot-${Date.now()}.png`
          };
          setPendingImages(prev => [...prev, newImage]);
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('See you next time!');
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!currentProject) {
      toast.error('Please select or create a project first');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    const ext = file.name.split('.').pop().toLowerCase();
    const allowedExts = ['pdf', 'pptx', 'ppt', 'docx', 'doc', 'csv', 'txt', 'xlsx', 'xls'];
    
    if (!allowedExts.includes(ext)) {
      toast.error(`File type .${ext} not supported. Use PDF, Word, PowerPoint, Excel, or CSV.`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'document');

    setUploading(true);
    try {
      await axios.post(`${API}/projects/${currentProject.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await fetchProject(currentProject.id);
      toast.success('Document uploaded successfully!');
      
      // Show prompt asking if they want to review - don't auto-analyze
      setShowDocumentPrompt(true);
      setUploadedDocType('Document');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload file';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  // Handle ask coach from guided mode
  const handleAskCoach = (question) => {
    setInputMessage(question);
    setTimeout(() => sendMessage(question), 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-500">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout" data-testid="dashboard-container">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar sidebar-dark grain-texture w-64 flex-shrink-0 flex flex-col ${sidebarOpen ? 'open' : ''}`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-500 rounded-md flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-slate-900" />
              </div>
              <span className="font-semibold text-white">SLP Coach</span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* New Project Button */}
        <div className="p-4">
          <Button 
            onClick={() => setShowOnboarding(true)}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium rounded-md"
            data-testid="new-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Change Project
          </Button>
          
          {/* Usage Stats & Upgrade for Free Users */}
          {usageStats?.is_free && (
            <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-400">Monthly usage</span>
                <span className="text-xs font-medium text-amber-400">
                  {usageStats.messages_used}/{usageStats.messages_limit}
                </span>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-1.5 mb-3">
                <div 
                  className="bg-amber-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.min((usageStats.messages_used / usageStats.messages_limit) * 100, 100)}%` }}
                />
              </div>
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                size="sm"
                className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                data-testid="upgrade-btn"
              >
                <Sparkles className="h-3 w-3 mr-2" />
                Upgrade for Unlimited
              </Button>
            </div>
          )}
        </div>

        {/* Projects List */}
        <div className="flex-1 overflow-hidden">
          <div className="px-4 py-2">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Your Projects</span>
          </div>
          <ScrollArea className="flex-1 px-2">
            <AnimatePresence>
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className={`group mb-2 rounded-md relative ${currentProject?.id === project.id ? 'bg-amber-500/20' : 'hover:bg-white/10'}`}
                  data-testid={`project-item-${project.id}`}
                >
                  <div 
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    onClick={() => navigate(`/dashboard/${project.id}`)}
                  >
                    <FolderOpen className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <span className="block truncate text-sm text-slate-200">{project.name}</span>
                      {/* Journey Progress Indicator */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              project.completion_percent >= 100 ? 'bg-emerald-500' :
                              project.completion_percent >= 70 ? 'bg-blue-500' :
                              project.completion_percent >= 40 ? 'bg-blue-400' :
                              'bg-blue-300'
                            }`}
                            style={{ width: `${project.completion_percent || 0}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium min-w-[28px] text-right ${
                          project.completion_percent >= 100 ? 'text-emerald-400' : 'text-blue-400'
                        }`}>
                          {project.completion_percent >= 100 ? '✓' : `${project.completion_percent || 0}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      className="p-1 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this project?')) {
                          deleteProject(project.id);
                        }
                      }}
                      data-testid={`delete-project-${project.id}`}
                      title="Delete project"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {projects.length === 0 && (
              <div className="px-4 py-8 text-center text-slate-500 text-sm">
                No projects yet.<br />Create one to get started!
              </div>
            )}
          </ScrollArea>

          {/* Change Load Dashboard - Available to all, preview for free users */}
          <div className="px-3 pb-2">
            <button
              onClick={() => navigate('/change-load')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/30 transition-all"
              data-testid="change-load-btn"
            >
              <BarChart3 className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-300">Change Load Dashboard</span>
              {!['pro', 'enterprise'].includes(user?.subscription_tier) && (
                <span className="ml-auto text-xs bg-amber-500/20 px-2 py-0.5 rounded text-amber-400">Preview</span>
              )}
            </button>
          </div>
        </div>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-slate-800/50 transition-colors">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-slate-300" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Admin link - only for admin emails */}
              {(user?.email === 'james@slpartners.au' || user?.email === 'james.crumpton23@gmail.com') && (
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-btn">
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Console
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content bg-slate-50">
        {/* Header */}
        <header className="glass border-b border-slate-200 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
              >
                <Menu className="h-5 w-5" />
              </button>
              {currentProject ? (
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-slate-500" />
                  <h1 className="font-semibold text-slate-900">{currentProject.name}</h1>
                </div>
              ) : (
                <h1 className="font-semibold text-slate-900">Dashboard</h1>
              )}
            </div>

            {currentProject && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-slate-200"
                  onClick={() => setShowGuidedMode(true)}
                  data-testid="guided-mode-btn"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Guided Mode
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-slate-200"
                      disabled={uploading}
                      data-testid="upload-document-btn"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="px-2 py-2 border-b border-slate-100">
                      <div className="flex items-start gap-2 text-xs text-slate-500">
                        <Shield className="h-3.5 w-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                        <span>Your documents are encrypted, stored securely, and only visible to you. We never share your data.</span>
                      </div>
                    </div>
                    <DropdownMenuItem onClick={() => handleFileUpload('document')} data-testid="upload-document-btn">
                      <FileText className="h-4 w-4 mr-2" />
                      Project Document
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.pptx,.ppt,.docx,.doc,.csv,.txt,.xlsx,.xls"
                  onChange={onFileSelected}
                />
                
                {/* Generate Documents Button - HIDDEN until toolkits ready */}
                {/* <Button
                  variant="outline"
                  onClick={() => setShowDocuments(true)}
                  className="gap-2"
                  data-testid="generate-documents-btn"
                >
                  <FileOutput className="h-4 w-4" />
                  Documents
                </Button> */}
              </div>
            )}
          </div>
        </header>

        {/* Content Area */}
        {currentProject ? (
          <div 
            className={`chat-container relative ${isDragging ? 'ring-2 ring-amber-400 ring-inset' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            ref={dropZoneRef}
          >
            {/* Drag overlay */}
            {isDragging && (
              <div className="absolute inset-0 bg-amber-50/90 z-30 flex items-center justify-center">
                <div className="text-center">
                  <UploadCloud className="h-12 w-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-lg font-medium text-amber-700">Drop to share your document</p>
                  <p className="text-sm text-amber-600 mb-3">PDF, Word, PowerPoint, Excel, or CSV</p>
                  <div className="flex items-center justify-center gap-2 text-xs text-amber-700/70">
                    <Shield className="h-3.5 w-3.5" />
                    <span>Encrypted & stored securely - only visible to you</span>
                  </div>
                </div>
              </div>
            )}

            {/* Journey Progress Bar */}
            <JourneyProgress 
              completedTasks={completedTasks}
              currentStage={journeyStage}
              compact={true}
              onStageClick={(stage) => {
                setSelectedInsightStage(stage);
                setShowInsights(true);
              }}
            />

            {/* Document Status Pills */}
            {(currentProject.business_case_file || currentProject.project_plan_file) && (
              <div className="px-6 py-2 bg-emerald-50 border-b border-emerald-100">
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-800">Documents uploaded</span>
                  {currentProject.business_case_file && (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      Document
                    </span>
                  )}
                  {currentProject.project_plan_file && (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      Document
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Messages */}
            <ScrollArea className="chat-messages">
              <div className="max-w-3xl mx-auto space-y-6">
                <AnimatePresence>
                  {messages.map((message, index) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`${
                        message.role === 'system' 
                          ? 'message-system px-4 py-3 rounded-lg' 
                          : ''
                      }`}
                      data-testid={`message-${message.role}-${index}`}
                    >
                      {message.role === 'user' ? (
                        <div className="flex justify-end">
                          <div className="message-user px-4 py-3 rounded-2xl rounded-br-md max-w-[85%]">
                            <p className="whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ) : message.role === 'assistant' ? (
                        <div className="flex gap-3">
                          <div className="w-9 h-9 bg-slate-900 rounded-xl flex-shrink-0 flex items-center justify-center">
                            <Lightbulb className="h-5 w-5 text-amber-400" />
                          </div>
                          <div className="message-assistant px-4 py-3 rounded-2xl rounded-tl-md max-w-[85%] prose-coach">
                            <MessageContent content={message.content} />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <MessageContent content={message.content} />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {sending && (
                  <motion.div 
                    className="flex gap-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="w-9 h-9 bg-slate-900 rounded-xl flex-shrink-0 flex items-center justify-center">
                      <Lightbulb className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="message-assistant px-4 py-3 rounded-2xl rounded-tl-md">
                      <div className="flex items-center gap-2 text-slate-500">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Stage-based Suggested Actions */}
            {showPrompts && !sending && (
              <motion.div 
                className="px-6 py-4 border-t border-slate-100 bg-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-sm text-slate-500 mb-3">Suggested next steps:</p>
                <div className="flex flex-wrap gap-2 max-w-3xl mx-auto">
                  {(STAGE_PROMPTS[journeyStage] || STAGE_PROMPTS.understand).map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (prompt.action === 'impact_assessment' || prompt.action === 'change_plan' || prompt.action === 'stakeholder_pack') {
                          setShowGuidedMode(true);
                        } else {
                          sendMessage(prompt.text);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full text-sm text-amber-800 font-medium transition-colors"
                      data-testid={`stage-prompt-${prompt.action}`}
                    >
                      <prompt.icon className="h-4 w-4" />
                      {prompt.text}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Document Upload Prompt - appears after uploading a doc */}
            {showDocumentPrompt && (
              <motion.div 
                className="px-6 py-4 border-t border-slate-100 bg-amber-50"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-sm text-amber-800 mb-3">
                  <strong>{uploadedDocType}</strong> uploaded. What would you like to do?
                </p>
                <div className="flex flex-wrap gap-2 max-w-3xl mx-auto">
                  <button
                    onClick={() => {
                      setShowDocumentPrompt(false);
                      sendMessage(`I've uploaded my ${uploadedDocType.toLowerCase()}. Can you have a look and share your initial thoughts?`);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-full text-sm text-amber-900 font-medium transition-colors"
                    data-testid="review-doc-btn"
                  >
                    <FileText className="h-4 w-4" />
                    Review my document
                  </button>
                  <button
                    onClick={() => {
                      setShowDocumentPrompt(false);
                      toast.success('Document saved. Continue when you\'re ready.');
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-full text-sm text-slate-700 font-medium transition-colors"
                    data-testid="continue-chat-btn"
                  >
                    Continue chatting
                  </button>
                </div>
              </motion.div>
            )}

            {/* Input */}
            <div className="chat-input-container">
              <div className="max-w-3xl mx-auto">
                {/* Action buttons above input */}
                <div className="flex justify-center gap-3 mb-3">
                  {/* Not Sure Button - shows after AI asks a question */}
                  {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && !sending && (
                    <button
                      onClick={() => sendMessage("I'm not sure")}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                      data-testid="not-sure-btn"
                    >
                      <HelpCircle className="h-4 w-4" />
                      I'm not sure — help me work through this
                    </button>
                  )}
                  
                  {/* Talk to SLP Button */}
                  <button
                    onClick={() => setShowContactSLP(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full transition-colors"
                    data-testid="talk-to-slp-btn"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Talk to SLP
                  </button>
                </div>
                
                {/* Pending Images Preview */}
                {pendingImages.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {pendingImages.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={img.preview} 
                          alt={img.name}
                          className="h-16 w-16 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-3">
                  {/* Image upload button */}
                  <Button
                    variant="outline"
                    onClick={() => imageInputRef.current?.click()}
                    className="h-12 w-12 rounded-xl border-slate-200"
                    title="Attach screenshot"
                    data-testid="attach-image-btn"
                  >
                    <Image className="h-5 w-5 text-slate-500" />
                  </Button>
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={onImageSelected}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  
                  <Textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handleImagePaste}
                    placeholder="Ask me anything about your change... (paste screenshots directly)"
                    className="flex-1 resize-none border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent min-h-[48px] max-h-[200px] rounded-xl"
                    rows={1}
                    disabled={sending}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={(!inputMessage.trim() && pendingImages.length === 0) || sending}
                    className="bg-slate-900 hover:bg-slate-800 h-12 w-12 rounded-xl"
                    data-testid="send-message-btn"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Press Enter to send • Paste or attach screenshots • Shift+Enter for new line
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center p-8">
            <motion.div 
              className="text-center max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-20 h-20 bg-amber-100 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                <Lightbulb className="h-10 w-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                Hi {user?.name?.split(' ')[0]}! 👋
              </h2>
              <p className="text-slate-600 mb-2">
                I'm your SLP Coach. I'll guide you through your change initiative step by step.
              </p>
              <p className="text-slate-500 text-sm mb-8">
                Let's start by understanding your change — I'll ask a few quick questions, then we'll build your roadmap together.
              </p>
              <Button 
                onClick={() => setShowOnboarding(true)}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 px-6"
                data-testid="empty-state-new-project-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Start My Change Journey
              </Button>
              
              {/* Pricing link for free users */}
              {usageStats?.is_free && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 block mx-auto text-sm text-slate-500 hover:text-amber-600 transition-colors"
                >
                  View pricing plans →
                </button>
              )}

              {/* Sample Project Ideas */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-4">
                  Common change initiatives people work on
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { title: 'System Implementation', desc: 'New CRM, ERP, or tech platform rollout' },
                    { title: 'Org Restructure', desc: 'Team changes, mergers, or new operating model' },
                    { title: 'Process Improvement', desc: 'Workflow changes, automation, or efficiency programs' }
                  ].map((idea, idx) => (
                    <button
                      key={idx}
                      onClick={() => setShowOnboarding(true)}
                      className="p-4 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors group"
                    >
                      <p className="font-medium text-slate-800 group-hover:text-amber-600 text-sm">{idea.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{idea.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </main>

      {/* Guided Mode Modal */}
      {showGuidedMode && currentProject && (
        <GuidedMode 
          projectId={currentProject.id}
          onClose={() => setShowGuidedMode(false)}
          onAskCoach={handleAskCoach}
        />
      )}

      {/* Project Onboarding Flow */}
      {showOnboarding && (
        <ProjectOnboarding
          onComplete={handleOnboardingComplete}
          onCancel={() => setShowOnboarding(false)}
        />
      )}

      {/* Insights Drawer */}
      {showInsights && currentProject && (
        <InsightsDrawer
          projectId={currentProject.id}
          stage={selectedInsightStage}
          onClose={() => setShowInsights(false)}
        />
      )}

      {/* Documents Panel */}
      {showDocuments && currentProject && (
        <DocumentsPanel
          projectId={currentProject.id}
          onClose={() => setShowDocuments(false)}
        />
      )}

      {/* Contact SLP Modal */}
      <ContactSLPModal
        isOpen={showContactSLP}
        onClose={() => setShowContactSLP(false)}
        user={user}
        projectName={currentProject?.name}
      />
    </div>
  );
}

// Helper component to render markdown-like content with polished steps
const MessageContent = ({ content }) => {
  // Parse inline formatting (bold, italic)
  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  // Parse non-step content (headings, bullets, paragraphs)
  const parseRegularContent = (lines) => {
    const elements = [];
    let currentList = [];

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`ul-${elements.length}`} className="list-disc ml-5 mb-3 space-y-1">
            {currentList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={index} className="font-semibold text-slate-900 mt-4 mb-2">{line.slice(4)}</h3>);
      } else if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={index} className="font-bold text-slate-900 mt-4 mb-2 text-lg">{line.slice(3)}</h2>);
      } else if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={index} className="font-bold text-slate-900 mt-4 mb-2 text-xl">{line.slice(2)}</h1>);
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        currentList.push(parseInline(line.slice(2)));
      } else if (line.trim() === '') {
        flushList();
      } else {
        flushList();
        elements.push(<p key={index} className="mb-2 leading-relaxed">{parseInline(line)}</p>);
      }
    });

    flushList();
    return elements;
  };

  // Check if content has numbered steps that should get the fancy treatment
  const stepsData = parseStepsFromContent(content);

  if (stepsData.hasSteps && stepsData.steps.length >= 2) {
    return (
      <div>
        {/* Content before steps */}
        {stepsData.beforeSteps.length > 0 && (
          <div className="mb-3">
            {parseRegularContent(stepsData.beforeSteps)}
          </div>
        )}
        
        {/* Polished steps display */}
        <StepsDisplay steps={stepsData.steps} />
        
        {/* Content after steps */}
        {stepsData.afterSteps.length > 0 && (
          <div className="mt-3">
            {parseRegularContent(stepsData.afterSteps)}
          </div>
        )}
      </div>
    );
  }

  // No steps detected - render normally
  return <div>{parseRegularContent(content.split('\n'))}</div>;
};
